"use client";

import React from "react";
import { ArrowRight, Loader, MapPin, Phone } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="146995" className="flex h-full flex-col items-center justify-center space-y-6 p-12 text-center animate-in fade-in duration-700">
        <div className="flex size-16 items-center justify-center rounded-none border-2 border-primary bg-primary/5">
          <ArrowRight className="size-6 text-primary" />
        </div>
        <div className="space-y-2">
          <h3 className="font-default text-xl font-bold uppercase tracking-tight text-foreground">
            Message Sent
          </h3>
          <p className="font-mono text-sm text-muted-foreground">
            {successMessage}
          </p>
        </div>
        <Button onClick={resetForm} variant="outline" className="mt-8 rounded-none border-primary font-mono text-xs uppercase tracking-widest text-primary hover:bg-primary hover:text-primary-foreground">
          Send Another
        </Button>
      </div>;
  }
  const inputArchitecturalClasses = "rounded-none border-x-0 border-t-0 border-b-2 border-border bg-transparent px-0 shadow-none focus-visible:border-primary focus-visible:ring-0 md:text-base font-mono placeholder:text-muted-foreground/30 transition-colors";
  return <Form onSubmit={handleSubmit} className="space-y-10">
      <div className="space-y-8">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                Your Name
              </FormLabel>
              <FormControl>
                <Input placeholder="Full Name" className={inputArchitecturalClasses} {...field} />
              </FormControl>
              <FormDescription className="font-mono text-[10px] text-muted-foreground/50">
                Required for project identification
              </FormDescription>
              <FormMessage className="font-mono text-xs text-destructive" />
            </FormItem>} />

        <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
          <FormField control={control} name="email" rules={{
          required: "Email is required",
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Invalid email format"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                  Email Address
                </FormLabel>
                <FormControl>
                  <Input type="email" placeholder="email@example.com" className={inputArchitecturalClasses} {...field} />
                </FormControl>
                <FormMessage className="font-mono text-xs text-destructive" />
              </FormItem>} />

          <FormField control={control} name="phone" rules={{
          pattern: {
            value: /^[0-9\-\+\s\(\)]*$/,
            message: "Invalid phone format"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                  Phone Number
                </FormLabel>
                <FormControl>
                  <Input type="tel" placeholder="(555) 000-0000" className={inputArchitecturalClasses} {...field} />
                </FormControl>
                <FormDescription className="font-mono text-[10px] text-muted-foreground/50">
                  For direct project coordination
                </FormDescription>
                <FormMessage className="font-mono text-xs text-destructive" />
              </FormItem>} />
        </div>

        <FormField control={control} name="message" rules={{
        required: "Message is required",
        minLength: {
          value: 10,
          message: "Minimum 10 characters"
        },
        maxLength: {
          value: 1000,
          message: "Maximum 1000 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                Project Details
              </FormLabel>
              <FormControl>
                <Textarea placeholder="Tell us about your concrete or masonry needs..." className={`min-h-[120px] resize-none ${inputArchitecturalClasses}`} {...field} />
              </FormControl>
              <FormMessage className="font-mono text-xs text-destructive" />
            </FormItem>} />

        <FormField control={control} name="subscribe" render={({
        field
      }) => <FormItem className="flex flex-row items-start space-x-4 space-y-0 pt-4">
              <FormControl>
                <input type="checkbox" className="mt-1 size-4 shrink-0 appearance-none border-2 border-border bg-transparent checked:bg-primary checked:border-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-all relative after:content-[''] after:absolute after:hidden checked:after:block after:left-[3px] after:top-[0px] after:w-[5px] after:h-[9px] after:border-r-2 after:border-b-2 after:border-primary-foreground after:rotate-45" checked={field.value} onChange={field.onChange} />
              </FormControl>
              <div className="space-y-2 leading-none">
                <FormLabel className="font-mono text-xs uppercase tracking-widest text-foreground cursor-pointer">
                  Stay updated on our projects
                </FormLabel>
                <FormDescription className="font-mono text-[10px] text-muted-foreground">
                  Receive insights on structural durability and new masonry work
                </FormDescription>
              </div>
            </FormItem>} />
      </div>

      {submitError && <div className="border border-destructive bg-destructive/5 p-4">
          <p className="font-mono text-xs text-destructive">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="h-14 w-full rounded-none bg-primary text-primary-foreground font-default text-sm font-bold uppercase tracking-[0.2em] hover:bg-primary/90 hover:scale-[1.01] transition-all">
        {isSubmitting ? <>
            <Loader className="mr-3 size-5 animate-spin" />
            PROCESSING...
          </> : "SEND MESSAGE"}
      </Button>
    </Form>;
}
export default function Contact() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return;
}